/* Admin page for the vAuto feed receiving structure.
   Lets admins paste/upload CSV/JSON/XML payloads, preview the diff,
   then publish — even before vAuto commits to a delivery method. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

const DELIVERY_METHODS = [
  { v: "", label: "Not chosen yet" },
  { v: "manual", label: "Manual paste / upload (for testing)" },
  { v: "vauto_push", label: "vAuto webhook push (we receive)" },
  { v: "scheduled_url", label: "We poll a scheduled URL" },
  { v: "sftp", label: "SFTP file drop" },
];

const FORMATS = ["csv", "json", "xml"];

const SAMPLE_CSV = `vin,stock_number,year,make,model,trim,price,mileage,exterior_color,interior_color,fuel_type,transmission,drivetrain,body_style,options,description
1G1FB1RX7K0SAMPLE1,T1001,2019,Chevrolet,Camaro,SS,28500,52000,Red,Black,Gas,Auto,RWD,Coupe,"Sunroof|Leather","Clean Camaro SS, well maintained"
1GCRYDED5LZSAMPLE2,T1002,2020,Chevrolet,Silverado,LT,38900,42000,Blue,Gray,Gas,Auto,4WD,Truck,"Tow Pkg","One owner, no accidents"`;

export default function AdminVautoFeed() {
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [canonical, setCanonical] = useState([]);
  const [ready, setReady] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [cfgDraft, setCfgDraft] = useState({});
  const [payload, setPayload] = useState(SAMPLE_CSV);
  const [fmt, setFmt] = useState("csv");
  const [previewResult, setPreviewResult] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const [runs, setRuns] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [tab, setTab] = useState("test");
  const [sftpBusy, setSftpBusy] = useState(false);
  const [sftpResult, setSftpResult] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailRunning, setEmailRunning] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);

  const loadEmailStatus = async () => {
    try {
      const r = await api.get("/admin/vauto-email/status");
      setEmailStatus(r.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadEmailStatus(); }, []);

  const runEmailImport = async () => {
    setEmailRunning(true); setError("");
    try {
      const r = await api.post("/admin/vauto-email/run-now");
      setEmailStatus({ last_run: r.data, active_vehicles_from_email: emailStatus?.active_vehicles_from_email });
      await loadEmailStatus();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setEmailRunning(false); }
  };

  const uploadVautoFile = async (file) => {
    if (!file) return;
    setUploadBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/vauto-email/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEmailStatus({ last_run: r.data, active_vehicles_from_email: emailStatus?.active_vehicles_from_email });
      await loadEmailStatus();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setUploadBusy(false); }
  };

  const testSftp = async () => {
    setSftpBusy(true); setError(""); setSftpResult(null);
    try {
      // Save first in case the user changed creds
      if (Object.keys(cfgDraft).length) await api.patch("/admin/vauto/config", cleanedDraft(cfgDraft));
      const r = await api.post("/admin/vauto/sftp/test");
      setSftpResult(r.data);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setSftpBusy(false); }
  };

  const pullSftp = async (publish) => {
    setSftpBusy(true); setError(""); setSftpResult(null);
    try {
      if (Object.keys(cfgDraft).length) await api.patch("/admin/vauto/config", cleanedDraft(cfgDraft));
      const r = await api.post(`/admin/vauto/sftp/pull-now?publish=${publish}`);
      setSftpResult(r.data);
      await loadAll();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setSftpBusy(false); }
  };

  const loadAll = async () => {
    try {
      const cfg = await api.get("/admin/vauto/config");
      const conf = cfg.data.config || {};
      // Pass through the masked password flag so the UI can hint "stored"
      setConfig(conf);
      setCfgDraft({ ...conf, _password_set: !!conf.sftp_password_set });
      setDefaults(cfg.data.default_header_map || {}); setCanonical(cfg.data.canonical_fields || []);
      setReady(cfg.data.ready);
      const r = await api.get("/admin/vauto/runs?limit=10");
      setRuns(r.data.items || []);
      const i = await api.get("/admin/vauto/inventory?limit=25");
      setInventory(i.data.items || []);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  useEffect(() => { loadAll(); }, []);

  // Strip UI-only flags (like `_password_set`) and empty-string password
  // (server treats empty string as "leave unchanged").
  const cleanedDraft = (d) => {
    const out = { ...d };
    delete out._password_set;
    delete out.sftp_password_set;     // server-side flag, never write back
    delete out.webhook_secret_set;
    delete out.webhook_secret_tail;
    if (out.sftp_password === "") delete out.sftp_password;
    if (out.webhook_secret === "") delete out.webhook_secret;
    return out;
  };

  const saveConfig = async () => {
    setSavingCfg(true); setError("");
    try {
      const { data } = await api.patch("/admin/vauto/config", cleanedDraft(cfgDraft));
      const conf = data.config || {};
      setConfig(conf);
      setReady(!!(conf.delivery_method && conf.feed_format));
      // Re-pull the masked password flag and clear the password input field
      setCfgDraft({ ...conf, _password_set: !!conf.sftp_password_set, sftp_password: "" });
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setSavingCfg(false); }
  };

  const runPreview = async (publish = false) => {
    if (publish) setPublishing(true); else setPreviewing(true);
    setError(""); setPreviewResult(null);
    try {
      const { data } = await api.post("/admin/vauto/preview", { payload, fmt, publish });
      setPreviewResult(data);
      if (publish) await loadAll();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setPublishing(false); setPreviewing(false); }
  };

  const inputCls = "w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500";

  return (
    <div className="bg-slate-50 min-h-full" data-testid="page-vauto-feed">
      <div className="bg-gradient-to-b from-[#061426] to-[#0a1f3a] text-white border-b border-white/10">
        <div className="px-5 py-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300 font-bold">Inventory</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold mt-0.5">vAuto Feed</h1>
          <p className="text-sm text-blue-200/70 mt-1">
            Receiving structure ready. The actual feed delivery (SFTP / webhook / scheduled URL) is wired once vAuto commits to a method.
            Use this page to test imports + configure header mappings without touching the live inventory.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${ready ? "bg-emerald-500/30 text-emerald-200" : "bg-amber-500/30 text-amber-200"}`}>
              {ready ? "✓ Receiving structure ready" : "Awaiting vAuto delivery method"}
            </span>
            <Link to="/admin/inventory" className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-white/10 text-white/80 hover:bg-white/20">← Vehicles</Link>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* ============ EMAIL ATTACHMENT IMPORTER ============ */}
        <div className="rounded-lg border-2 border-[#006DFF]/40 bg-blue-50 p-4" data-testid="vauto-email-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#006DFF]">Recommended Setup</div>
              <h2 className="text-lg font-black text-[#061426] mt-1">Daily Email Inventory Import</h2>
              <p className="text-sm text-gray-700 mt-1">
                vAuto sends an inventory export (.htm or .csv) to <code className="bg-white px-1 rounded">askscott@scottsellschevys.com</code> daily.
                The importer auto-runs at 5:30 AM ET, parses the file, upserts vehicles by VIN, and marks missing VINs as sold.
                You can also <b>upload a vAuto export directly</b> to update inventory right now.
              </p>
            </div>
            <div className="flex flex-col gap-2 items-stretch">
              <button
                onClick={runEmailImport}
                disabled={emailRunning}
                data-testid="vauto-email-run-now"
                className="bg-[#006DFF] hover:bg-blue-700 text-white text-sm font-bold px-4 py-2 rounded disabled:opacity-50 whitespace-nowrap"
              >
                {emailRunning ? "Running…" : "Run Email Import Now"}
              </button>
              <label
                className={`bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold px-4 py-2 rounded text-center cursor-pointer whitespace-nowrap ${uploadBusy ? "opacity-50 pointer-events-none" : ""}`}
                data-testid="vauto-upload-label"
              >
                {uploadBusy ? "Uploading…" : "Upload vAuto File"}
                <input
                  type="file"
                  className="hidden"
                  accept=".htm,.html,.csv,.txt,.xls,.xlsx"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadVautoFile(f);
                    e.target.value = "";
                  }}
                  data-testid="vauto-upload-input"
                  disabled={uploadBusy}
                />
              </label>
            </div>
          </div>
          {emailStatus?.last_run && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-7 gap-2 text-sm" data-testid="vauto-email-status">
              <Stat label="Last Run" value={(emailStatus.last_run.finished_at || emailStatus.last_run.started_at || "").slice(0, 16).replace("T", " ")} />
              <Stat label="Status" value={emailStatus.last_run.ok ? "OK" : "Failed"} accent={emailStatus.last_run.ok ? "emerald" : "red"} />
              <Stat label="Added" value={emailStatus.last_run.added ?? 0} accent="emerald" />
              <Stat label="Updated" value={emailStatus.last_run.updated ?? 0} accent="blue" />
              <Stat label="Marked Sold" value={emailStatus.last_run.marked_sold ?? 0} accent="amber" />
              <Stat label="New" value={emailStatus.last_run.new_count ?? 0} />
              <Stat label="Used" value={emailStatus.last_run.used_count ?? 0} />
            </div>
          )}
          {emailStatus?.last_run?.change_summary && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm" data-testid="vauto-change-summary">
              <Stat label="Price Changes" value={emailStatus.last_run.change_summary.price_change_count ?? 0} accent="amber" />
              <Stat label="Added Vehicles" value={emailStatus.last_run.change_summary.added_count ?? 0} accent="emerald" />
              <Stat label="Sold / Removed" value={emailStatus.last_run.change_summary.sold_count ?? 0} accent="red" />
              <Stat label="Rebate/Offer Changes" value={emailStatus.last_run.change_summary.rebate_changes ?? 0} accent="blue" />
            </div>
          )}
          {emailStatus?.last_run?.change_summary?.rebate_note && (
            <div className="mt-2 text-[11px] text-gray-600 italic" data-testid="vauto-rebate-note">
              <b>Rebate / incentive note:</b> {emailStatus.last_run.change_summary.rebate_note}
            </div>
          )}
          {emailStatus && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm" data-testid="vauto-active-totals">
              <Stat label="Active total" value={emailStatus.active_vehicles_from_email ?? 0} accent="blue" />
              <Stat label="Active New" value={emailStatus.new_count_active ?? 0} />
              <Stat label="Active Used" value={emailStatus.used_count_active ?? 0} />
              <Stat label="Open Alerts" value={emailStatus.open_alert_count ?? 0} accent={emailStatus.open_alert_count ? "red" : "emerald"} />
            </div>
          )}
          {emailStatus?.last_run?.file_name && (
            <div className="mt-2 text-xs text-gray-600">
              Latest file: <code className="bg-white px-1 rounded">{emailStatus.last_run.file_name}</code>
              {" · "}{emailStatus.last_run.rows_seen ?? 0} rows
              {emailStatus.last_run.failed_row_count > 0 && (
                <> · <span className="text-red-600 font-semibold">{emailStatus.last_run.failed_row_count} failed</span></>
              )}
              {emailStatus.last_successful_run && (
                <> · last success: <b>{(emailStatus.last_successful_run.finished_at || "").slice(0,16).replace("T"," ")}</b></>
              )}
            </div>
          )}
          {emailStatus?.last_run?.errors?.length > 0 && (
            <div className="mt-2 bg-amber-50 border border-amber-300 text-amber-900 text-xs rounded p-2" data-testid="vauto-email-errors">
              <b>Errors:</b>
              <ul className="list-disc ml-5 mt-1">
                {emailStatus.last_run.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
          {emailStatus?.last_run?.failed_rows?.length > 0 && (
            <details className="mt-2 bg-red-50 border border-red-200 text-red-900 text-xs rounded p-2" data-testid="vauto-email-failed-rows">
              <summary className="font-bold cursor-pointer">Failed rows ({emailStatus.last_run.failed_row_count})</summary>
              <ul className="list-disc ml-5 mt-1">
                {emailStatus.last_run.failed_rows.slice(0, 10).map((r, i) => (
                  <li key={i}>Row {r.row}: {r.reason}</li>
                ))}
              </ul>
            </details>
          )}
          {!emailStatus?.last_run && (
            <div className="mt-2 text-xs text-gray-600 italic">No imports run yet. Click &ldquo;Run Email Import Now&rdquo; once vAuto has sent the first email, or use &ldquo;Upload vAuto File&rdquo; to import a CSV manually.</div>
          )}
        </div>

        {/* Tab strip */}
        <div className="flex flex-wrap gap-1 border-b border-gray-200">
          {[{ k: "test", label: "Test Import" }, { k: "config", label: "Configuration" }, { k: "runs", label: "Run History" }, { k: "inv", label: "Preview Inventory" }].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`text-sm px-4 py-2 border-b-2 ${tab === t.k ? "border-[#006DFF] text-[#006DFF] font-semibold" : "border-transparent text-gray-600 hover:text-[#061426]"}`}
              data-testid={`vauto-tab-${t.k}`}>
              {t.label}
            </button>
          ))}
        </div>

        {error && <div className="bg-red-50 border border-red-300 text-red-800 text-sm rounded p-3" data-testid="vauto-error">{error}</div>}

        {/* TEST IMPORT */}
        {tab === "test" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <h3 className="text-sm font-bold text-[#061426] mb-2">1. Paste a sample feed</h3>
              <div className="flex gap-2 mb-2">
                <select value={fmt} onChange={e => setFmt(e.target.value)} className={inputCls + " w-32"} data-testid="vauto-fmt">
                  {FORMATS.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                </select>
                <button onClick={() => setPayload(SAMPLE_CSV)} className="text-xs px-3 py-2 rounded border border-gray-300 hover:bg-gray-50" data-testid="vauto-load-sample">Load sample CSV</button>
                <button onClick={() => setPayload("")} className="text-xs px-3 py-2 rounded border border-gray-300 hover:bg-gray-50">Clear</button>
              </div>
              <textarea value={payload} onChange={e => setPayload(e.target.value)} rows={14}
                className="w-full border border-gray-300 rounded px-3 py-2 text-xs font-mono focus:outline-none focus:border-blue-500"
                placeholder={`Paste ${fmt.toUpperCase()} payload here…`} data-testid="vauto-payload" />
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => runPreview(false)} disabled={previewing || !payload}
                  className="bg-[#061426] hover:bg-[#0a1f3a] text-white text-xs font-bold px-4 py-2 rounded disabled:opacity-50" data-testid="vauto-preview-btn">
                  {previewing ? "Previewing…" : "Preview (dry-run)"}
                </button>
                <button onClick={() => { if (window.confirm("Publish this feed to db.vauto_inventory? This will insert / update / mark-sold vehicles.")) runPreview(true); }}
                  disabled={publishing || !payload}
                  className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-4 py-2 rounded disabled:opacity-50" data-testid="vauto-publish-btn">
                  {publishing ? "Publishing…" : "Publish to inventory"}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <h3 className="text-sm font-bold text-[#061426] mb-2">2. Diff Preview</h3>
              {!previewResult ? (
                <p className="text-xs text-gray-500">Preview results show here after you run a dry-run.</p>
              ) : (
                <div className="space-y-2 text-sm" data-testid="vauto-result">
                  <div className="grid grid-cols-2 gap-2">
                    <Stat label="Raw rows" value={previewResult.counts.raw_rows} />
                    <Stat label="Valid rows" value={previewResult.counts.valid_rows} />
                    <Stat label="To insert" value={previewResult.counts.to_insert} accent="emerald" />
                    <Stat label="To update" value={previewResult.counts.to_update} accent="blue" />
                    <Stat label="To mark sold" value={previewResult.counts.to_mark_sold} accent="amber" />
                    <Stat label="Row errors" value={previewResult.counts.row_errors} accent={previewResult.counts.row_errors > 0 ? "red" : null} />
                  </div>
                  {previewResult.row_errors?.length > 0 && (
                    <details className="bg-red-50 border border-red-200 rounded p-2 text-xs" open>
                      <summary className="font-bold text-red-700">Row errors ({previewResult.row_errors.length})</summary>
                      <ul className="mt-1 space-y-1">
                        {previewResult.row_errors.slice(0, 10).map((e, i) => (
                          <li key={i}><b>Row {e.row_index}:</b> {e.warnings.join("; ")}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                  {previewResult.diff_preview?.to_insert?.length > 0 && (
                    <details className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs">
                      <summary className="font-bold text-emerald-700">Will insert ({previewResult.diff_preview.to_insert.length})</summary>
                      <ul className="mt-1 space-y-0.5">
                        {previewResult.diff_preview.to_insert.slice(0, 10).map((v, i) => (
                          <li key={i}>{v.year} {v.make} {v.model} · VIN <span className="font-mono">{v.vin}</span> · ${v.price?.toLocaleString()}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                  {previewResult.diff_preview?.to_update?.length > 0 && (
                    <details className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
                      <summary className="font-bold text-blue-700">Will update ({previewResult.diff_preview.to_update.length})</summary>
                      <ul className="mt-1 space-y-0.5">
                        {previewResult.diff_preview.to_update.map((d, i) => (
                          <li key={i}>VIN <span className="font-mono">{d.row.vin}</span> · changed: {d.changed_fields.join(", ")}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                  {previewResult.published && (
                    <div className="bg-emerald-100 border border-emerald-300 rounded p-2 text-xs font-semibold text-emerald-900" data-testid="vauto-published">
                      ✓ Published: {previewResult.published.inserted} inserted, {previewResult.published.updated} updated, {previewResult.published.marked_sold} marked sold.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* CONFIG */}
        {tab === "config" && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm max-w-3xl">
            <h3 className="text-sm font-bold text-[#061426] mb-3">Feed delivery & schedule</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">Delivery method</div>
                <select value={cfgDraft.delivery_method || ""} onChange={e => setCfgDraft({ ...cfgDraft, delivery_method: e.target.value })} className={inputCls} data-testid="vauto-cfg-delivery">
                  {DELIVERY_METHODS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">Feed format</div>
                <select value={cfgDraft.feed_format || ""} onChange={e => setCfgDraft({ ...cfgDraft, feed_format: e.target.value })} className={inputCls} data-testid="vauto-cfg-fmt">
                  <option value="">— TBD —</option>
                  {FORMATS.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                </select>
              </label>
              {cfgDraft.delivery_method === "sftp" && (
                <>
                  <label className="block">
                    <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">SFTP host</div>
                    <input value={cfgDraft.sftp_host || ""} onChange={e => setCfgDraft({ ...cfgDraft, sftp_host: e.target.value })} className={inputCls} placeholder="sftp.vauto.com" data-testid="vauto-cfg-sftp-host" />
                  </label>
                  <label className="block">
                    <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">Port</div>
                    <input type="number" value={cfgDraft.sftp_port || 22} onChange={e => setCfgDraft({ ...cfgDraft, sftp_port: parseInt(e.target.value || "22", 10) })} className={inputCls} placeholder="22" />
                  </label>
                  <label className="block">
                    <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">SFTP username</div>
                    <input value={cfgDraft.sftp_username || ""} onChange={e => setCfgDraft({ ...cfgDraft, sftp_username: e.target.value })} className={inputCls} placeholder="MP2703 or vAuto-assigned" data-testid="vauto-cfg-sftp-user" />
                  </label>
                  <label className="block">
                    <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">
                      Password {cfgDraft._password_set && <span className="text-[10px] text-emerald-700 ml-2">(stored — leave blank to keep)</span>}
                    </div>
                    <input type="password" value={cfgDraft.sftp_password || ""} onChange={e => setCfgDraft({ ...cfgDraft, sftp_password: e.target.value })} className={inputCls + " font-mono"} placeholder={cfgDraft._password_set ? "•••••• (leave blank to keep)" : "vAuto-issued password"} data-testid="vauto-cfg-sftp-pwd" />
                  </label>
                  <label className="block col-span-2">
                    <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">Remote folder path</div>
                    <input value={cfgDraft.sftp_remote_path || ""} onChange={e => setCfgDraft({ ...cfgDraft, sftp_remote_path: e.target.value })} className={inputCls + " font-mono"} placeholder="/MP2703/ or /feeds/byerschevy/" />
                  </label>
                  <label className="block col-span-2">
                    <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">Filename pattern (regex)</div>
                    <input value={cfgDraft.sftp_filename_pattern || ""} onChange={e => setCfgDraft({ ...cfgDraft, sftp_filename_pattern: e.target.value })} className={inputCls + " font-mono"} placeholder="MP2703_inventory.*\.csv$" />
                    <div className="text-[10px] text-gray-400 mt-0.5">Default <code>MP2703_inventory.*\.csv$</code> — picks the newest matching file in the folder.</div>
                  </label>
                </>
              )}
              {cfgDraft.delivery_method === "scheduled_url" && (
                <label className="block col-span-2">
                  <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">Feed URL</div>
                  <input value={cfgDraft.feed_url || ""} onChange={e => setCfgDraft({ ...cfgDraft, feed_url: e.target.value })} className={inputCls} placeholder="https://feeds.vauto.com/byers/inventory.json" />
                </label>
              )}
              {cfgDraft.delivery_method === "vauto_push" && (
                <label className="block col-span-2">
                  <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">Webhook secret (shared with vAuto)</div>
                  <input value={cfgDraft.webhook_secret || ""} onChange={e => setCfgDraft({ ...cfgDraft, webhook_secret: e.target.value })} className={`${inputCls} font-mono`} placeholder="randomly-generated-shared-secret" />
                  <div className="text-[10px] text-gray-500 mt-1">vAuto posts to <code>/api/integrations/vauto/webhook</code> with this secret.</div>
                </label>
              )}
              <label className="block col-span-2">
                <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">Contact at vAuto</div>
                <input value={cfgDraft.contact_email || ""} onChange={e => setCfgDraft({ ...cfgDraft, contact_email: e.target.value })} className={inputCls} placeholder="email or phone" />
              </label>
              <label className="block col-span-2">
                <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">Notes</div>
                <textarea value={cfgDraft.notes || ""} onChange={e => setCfgDraft({ ...cfgDraft, notes: e.target.value })} rows={2} className={inputCls} />
              </label>
              <label className="block col-span-2 flex items-center gap-2">
                <input type="checkbox" checked={!!cfgDraft.auto_publish} onChange={e => setCfgDraft({ ...cfgDraft, auto_publish: e.target.checked })} data-testid="vauto-cfg-autopublish" />
                <span className="text-sm">Auto-publish when feed arrives (uncheck for preview-only)</span>
              </label>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={saveConfig} disabled={savingCfg}
                className="bg-[#061426] hover:bg-[#0a1f3a] text-white text-xs font-bold px-4 py-2 rounded disabled:opacity-50" data-testid="vauto-cfg-save">
                {savingCfg ? "Saving…" : "Save configuration"}
              </button>
              {cfgDraft.delivery_method === "sftp" && (
                <>
                  <button onClick={testSftp} disabled={sftpBusy}
                    className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-4 py-2 rounded disabled:opacity-50" data-testid="vauto-sftp-test">
                    {sftpBusy ? "Testing…" : "Test connection"}
                  </button>
                  <button onClick={() => pullSftp(false)} disabled={sftpBusy}
                    className="bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-bold px-4 py-2 rounded disabled:opacity-50" data-testid="vauto-sftp-pull-preview">
                    {sftpBusy ? "Pulling…" : "Pull now (preview)"}
                  </button>
                  <button onClick={() => { if (window.confirm("Pull AND publish the feed now? This will update db.vauto_inventory.")) pullSftp(true); }} disabled={sftpBusy}
                    className="bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold px-4 py-2 rounded disabled:opacity-50" data-testid="vauto-sftp-pull-publish">
                    Pull + publish now
                  </button>
                </>
              )}
            </div>
            {sftpResult && (
              <div className={`mt-3 rounded p-2 text-xs border ${sftpResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-red-50 border-red-200 text-red-900"}`} data-testid="vauto-sftp-result">
                <div className="font-bold mb-1">{sftpResult.ok ? "✓ SFTP OK" : "✕ SFTP failed"}</div>
                {sftpResult.error && <div>Error: <code>{sftpResult.error}</code></div>}
                {sftpResult.folder && (
                  <div className="space-y-0.5">
                    <div>Folder: <code>{sftpResult.folder}</code> — {sftpResult.entries_total} entries</div>
                    {sftpResult.matching_files && <div>Matching files: <code>{(sftpResult.matching_files || []).join(", ") || "(none)"}</code></div>}
                    {sftpResult.sample_files && <div className="text-[10px] text-gray-500">Sample: {(sftpResult.sample_files || []).join(" · ")}</div>}
                  </div>
                )}
                {sftpResult.filename && (
                  <div className="mt-1 space-y-0.5">
                    <div>Picked: <code>{sftpResult.filename}</code> ({(sftpResult.size_bytes || 0).toLocaleString()} bytes)</div>
                    {sftpResult.counts && <div>Counts: insert {sftpResult.counts.to_insert} · update {sftpResult.counts.to_update} · mark-sold {sftpResult.counts.to_mark_sold} · errors {sftpResult.counts.row_errors}</div>}
                    {sftpResult.published && <div className="font-bold">✓ Published: {sftpResult.published.inserted} inserted, {sftpResult.published.updated} updated, {sftpResult.published.marked_sold} marked sold.</div>}
                  </div>
                )}
              </div>
            )}
            {(config?.last_sftp_pull_at || config?.last_sftp_filename) && (
              <div className="mt-3 text-xs text-gray-500 border-t border-gray-200 pt-2" data-testid="vauto-sftp-last-pull">
                Last SFTP pull: <code>{config.last_sftp_pull_at}</code>
                {config.last_sftp_filename && <> · file <code>{config.last_sftp_filename}</code></>}
                {config.last_sftp_pull_status && <> · status <b>{config.last_sftp_pull_status}</b></>}
                {config.last_sftp_pull_error && <div className="text-red-600 mt-0.5">{config.last_sftp_pull_error}</div>}
              </div>
            )}
            <div className="mt-4 text-xs text-gray-600 border-t border-gray-200 pt-3">
              <div className="font-bold text-[#061426] mb-1">Canonical fields ({canonical.length})</div>
              <div className="font-mono text-[10px] text-gray-500">{canonical.join(", ")}</div>
              <div className="font-bold text-[#061426] mt-2 mb-1">Default header map ({Object.keys(defaults).length})</div>
              <details>
                <summary className="cursor-pointer text-blue-700">Show built-in mappings</summary>
                <pre className="font-mono text-[10px] mt-2 bg-gray-50 p-2 rounded max-h-60 overflow-auto">{JSON.stringify(defaults, null, 2)}</pre>
              </details>
            </div>
          </div>
        )}

        {/* RUN HISTORY */}
        {tab === "runs" && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm" data-testid="vauto-runs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-gray-500">
                <tr><th className="text-left px-3 py-2">Run</th><th className="text-left px-3 py-2">Started</th><th className="text-left px-3 py-2">By</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Counts</th></tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-mono text-xs">{r.id?.slice(0, 8)}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{(r.started_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2 text-xs">{r.started_by}</td>
                    <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${r.status === "published" ? "bg-emerald-100 text-emerald-700" : r.status === "error" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{r.status}</span></td>
                    <td className="px-3 py-2 text-xs font-mono">
                      {r.counts ? `+${r.counts.to_insert} ~${r.counts.to_update} −${r.counts.to_mark_sold} !${r.counts.row_errors}` : "—"}
                    </td>
                  </tr>
                ))}
                {runs.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-gray-400">No runs yet — try Test Import.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* INVENTORY */}
        {tab === "inv" && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm" data-testid="vauto-inventory">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-gray-500">
                <tr><th className="text-left px-3 py-2">VIN</th><th className="text-left px-3 py-2">Stock</th><th className="text-left px-3 py-2">Vehicle</th><th className="text-right px-3 py-2">Price</th><th className="text-right px-3 py-2">Miles</th><th className="text-left px-3 py-2">Feed</th><th className="text-left px-3 py-2">Seen</th></tr>
              </thead>
              <tbody>
                {inventory.map(v => (
                  <tr key={v.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-mono text-xs">{v.vin}</td>
                    <td className="px-3 py-2 font-mono text-xs">{v.stock_number}</td>
                    <td className="px-3 py-2 text-sm">{v.year} {v.make} {v.model} {v.trim || ""}</td>
                    <td className="px-3 py-2 text-right text-sm">${(v.price || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-xs text-gray-500">{(v.mileage || 0).toLocaleString()}</td>
                    <td className="px-3 py-2"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${v.feed_status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{v.feed_status}</span></td>
                    <td className="px-3 py-2 text-xs text-gray-500">{(v.last_seen_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
                {inventory.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-gray-400">No vehicles in the preview inventory yet — publish a feed first.</td></tr>}
              </tbody>
            </table>
            <div className="bg-slate-50 px-3 py-2 border-t border-gray-200 text-[11px] text-gray-600">
              This is the staging inventory <code>db.vauto_inventory</code>. The vAuto email importer now writes directly to <code>db.vehicles</code>, which the public site reads.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const c = accent === "emerald" ? "text-emerald-600" :
            accent === "blue" ? "text-blue-600" :
            accent === "amber" ? "text-amber-600" :
            accent === "red" ? "text-red-600" : "text-[#061426]";
  return (
    <div className="bg-slate-50 border border-gray-200 rounded p-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-lg font-bold ${c}`}>{value ?? 0}</div>
    </div>
  );
}
